import React, { useState } from 'react';

const ReviewsTab = ({ course }) => {
  const reviews = course.feedbacks || [];
  const [replyText, setReplyText] = useState({});
  const [replyingTo, setReplyingTo] = useState(null);
  const [replies, setReplies] = useState({});

  const calculateAverageRating = () => {
    if (reviews.length === 0) return 0;
    const totalRating = reviews.reduce((sum, review) => sum + (review.rating || 0), 0);
    return parseFloat((totalRating / reviews.length).toFixed(1));
  };

  const averageRating = calculateAverageRating();

  const handleReplyClick = (reviewId) => {
    setReplyingTo(replyingTo === reviewId ? null : reviewId);
    if (!replies[reviewId]) {
      setReplyText(prev => ({ ...prev, [reviewId]: '' }));
    }
  };

  const handleReplySubmit = (reviewId) => {
    if (replyText[reviewId]?.trim()) {
      const newReply = {
        id: Date.now(),
        text: replyText[reviewId],
        author: 'Instructor',
        date: new Date().toISOString(),
        isInstructor: true
      };
      
      setReplies(prev => ({
        ...prev,
        [reviewId]: [...(prev[reviewId] || []), newReply]
      }));
      
      setReplyText(prev => ({ ...prev, [reviewId]: '' }));
      setReplyingTo(null);
    }
  };

  const handleReplyChange = (reviewId, text) => {
    setReplyText(prev => ({ ...prev, [reviewId]: text }));
  };

  const handleCancelReply = (reviewId) => {
    setReplyingTo(null);
    setReplyText(prev => ({ ...prev, [reviewId]: '' }));
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-900">Student Reviews</h3>
          <p className="text-gray-600">{reviews.length} reviews • Average rating: {averageRating}/5</p>
        </div>
      </div>
      
      {reviews.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No reviews yet</h3>
          <p className="text-gray-600">Students haven't left any reviews for this course yet.</p>
        </div>
      ) : (
        <div className="space-y-6">
          {reviews.map((review) => (
            <div key={review.id} className="border border-gray-200 rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h4 className="font-semibold text-gray-900">
                    {review.user?.name || `Student ${review.userId}`}
                  </h4>
                  <div className="flex items-center space-x-1 mt-1">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className={`w-4 h-4 ${i < (review.rating || 0) ? 'text-yellow-400' : 'text-gray-300'}`}
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                    <span className="ml-2 text-sm text-gray-600">
                      {(review.rating || 0).toFixed(1)} out of 5
                    </span>
                  </div>
                </div>
                {review.createdAt && (
                  <span className="text-sm text-gray-500">
                    {new Date(review.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    })}
                  </span>
                )}
              </div>
              <p className="text-gray-700 whitespace-pre-wrap mb-4">{review.comment || 'No comment provided'}</p>
              
              {/* Replies Section */}
              {replies[review.id] && replies[review.id].length > 0 && (
                <div className="ml-4 pl-4 border-l-2 border-green-200 space-y-3 mb-4">
                  {replies[review.id].map((reply) => (
                    <div key={reply.id} className="bg-green-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-green-700">{reply.author}</span>
                          {reply.isInstructor && (
                            <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded">
                              Instructor
                            </span>
                          )}
                        </div>
                        <span className="text-sm text-gray-500">
                          {new Date(reply.date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <p className="text-gray-700 whitespace-pre-wrap">{reply.text}</p>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Reply Button and Form */}
              <div className="flex items-center justify-end">
                <button
                  onClick={() => handleReplyClick(review.id)}
                  className="px-4 py-2 text-sm font-medium text-green-600 bg-green-50 rounded-lg hover:bg-green-100 transition-colors duration-300 flex items-center space-x-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
                  </svg>
                  <span>Reply</span>
                </button>
              </div>
              
              {/* Reply Input Form */}
              {replyingTo === review.id && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <textarea
                    value={replyText[review.id] || ''}
                    onChange={(e) => handleReplyChange(review.id, e.target.value)}
                    placeholder="Write your reply..."
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                    rows="3"
                  />
                  <div className="flex justify-end space-x-2 mt-3">
                    <button
                      onClick={() => handleCancelReply(review.id)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-300"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleReplySubmit(review.id)}
                      disabled={!replyText[review.id]?.trim()}
                      className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors duration-300 ${
                        replyText[review.id]?.trim()
                          ? 'bg-green-500 hover:bg-green-600'
                          : 'bg-gray-300 cursor-not-allowed'
                      }`}
                    >
                      Post Reply
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {reviews.length > 0 && (
        <div className="mt-8 bg-gray-50 rounded-xl p-6">
          <h4 className="font-semibold text-gray-900 mb-4">Rating Distribution</h4>
          <div className="space-y-3">
            {[5, 4, 3, 2, 1].map((star) => {
              const count = reviews.filter(review => Math.round(review.rating || 0) === star).length;
              const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
              
              return (
                <div key={star} className="flex items-center">
                  <div className="w-16 text-sm text-gray-600">
                    {star} star{star !== 1 ? 's' : ''}
                  </div>
                  <div className="flex-1 mx-4">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-yellow-500 h-2 rounded-full" 
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                  <div className="w-12 text-sm text-gray-600 text-right">
                    {count} ({percentage.toFixed(0)}%)
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewsTab;